import { type Colors, type SpacingKeys } from '@kijiji/theme'
import { type TFunction } from 'next-i18next'

import { type SearchPageType } from '@/lib/ga/events/search'
import { type Listing } from '@/types/search'
import { BodyText } from '@/ui/atoms/body-text'
import { Spacing } from '@/ui/atoms/spacing'
import { TextLink } from '@/ui/atoms/text-link'

import { LinkCustom } from '../../shared/link-custom/LinkCustom'
import { RenderListingCard } from './RenderListingCard'
import { type SearchListContainerProps, SearchListContainer, TopAdsContainer } from './styled'

type RenderTopAdsProps = {
  topAds: Listing[]
  trackTopAdLearnMoreClick: () => void
  isCarsTrucksSearch: boolean
  autosSearchListContainerGap: SearchListContainerProps['gap']
  listings: Listing[]
  globalKeyword: string
  isCalgarySearch: boolean
  searchPageType: SearchPageType
  isMobile?: boolean
  handleSeeAllTopAds: () => void
  colors: Colors
  spacing: Record<SpacingKeys, string>
  t: TFunction
}

export const RenderTopAds = ({
  topAds,
  trackTopAdLearnMoreClick,
  isCarsTrucksSearch,
  autosSearchListContainerGap,
  listings,
  globalKeyword,
  isCalgarySearch,
  searchPageType,
  isMobile,
  handleSeeAllTopAds,
  colors,
  spacing,
  t,
}: RenderTopAdsProps) => {
  if (!topAds.length) return null

  return (
    <TopAdsContainer>
      <Spacing mBottom={spacing.default}>
        <BodyText size="medium" color={colors.grey.primary}>
          {t('srp:listing.top_ad.label')}{' '}
          <LinkCustom
            aria-label={t('srp:listing.top_ad.learn_more_long_label')}
            href={t('srp:listing.top_ad.learn_more_link')}
            hasUnderline
            onClick={trackTopAdLearnMoreClick}
            variant="secondary"
          >
            {t('common:labels.learn_more')}
          </LinkCustom>
        </BodyText>
      </Spacing>

      <SearchListContainer
        data-testid="top-ads-list"
        gap={isCarsTrucksSearch ? autosSearchListContainerGap : undefined}
      >
        {topAds.map((item, index) => (
          <RenderListingCard
            key={item.id}
            item={item}
            index={index}
            isTopAd={true}
            listings={listings}
            globalKeyword={globalKeyword}
            isCarsTrucksSearch={isCarsTrucksSearch}
            isCalgarySearch={isCalgarySearch}
            searchPageType={searchPageType}
            isMobile={isMobile}
          />
        ))}
      </SearchListContainer>

      <Spacing mTop={spacing.default}>
        <TextLink
          as="button"
          hasUnderline
          onClick={handleSeeAllTopAds}
          variant="secondary"
          data-testid="see-all-top-ads"
        >
          {t('common:labels.see_all')}
        </TextLink>
      </Spacing>
    </TopAdsContainer>
  )
}
